import React, {Fragment, useMemo} from "react";

import {DishDetailsItem} from "@apps/dish/interfaces";

import RatingStars from "@components/RatingStars";

import styles from "./DishReviews.scss";
import ReviewListItem from "./ReviewListItem/ReviewListItem";
import useDishReviews from "./useDishReviews";

type DishReviewsProps = {
  dish: DishDetailsItem;
};

export default function DishReviews({dish}: DishReviewsProps): JSX.Element {
  const {data, fetchNextPage, hasNextPage, isLoading} = useDishReviews(dish.id);
  const showNoReviewsMessage =
    (!data?.length || !data?.[0].results?.length) && !isLoading;

  const userRatingMessage = useMemo(() => {
    if (dish.user_rating) {
      if (dish.number_user_reviews && dish.number_user_reviews > 1) {
        return `Your average rating: ${dish.user_rating.toFixed(2)}`;
      } else if (dish.number_user_reviews && dish.number_user_reviews === 1) {
        return `Your rating: ${dish.user_rating}`;
      }
    }
    return "";
  }, [dish]);

  return (
    <div className={styles.dishReviews}>
      <div className={styles.reviewsHead}>
        <h2 className={styles.reviewsTitle}>Reviews</h2>

        {showNoReviewsMessage && (
          <div className={styles.noReviewsMessage}>
            <span>There are no reviews for this dish yet</span>
          </div>
        )}

        <div className={styles.reviewsScoreBar}>
          <span aria-hidden="true">
            {dish.average_rating?.toFixed(1) ?? 0}
            &nbsp;
          </span>
          <RatingStars
            className={styles.reviewStars}
            averageRating={dish.average_rating ?? 0}
          />
          <span className="reviews-count">
            &nbsp;
            {dish.review_count} Ratings
          </span>
        </div>
      </div>
      {userRatingMessage && (
        <div className={styles.userReviewsScore}>{userRatingMessage}</div>
      )}
      <ul className={styles.reviewsContainer}>
        {data?.map((page) => (
          <Fragment key={page.page_number}>
            {page.results?.map((review) => (
              <ReviewListItem review={review} key={review.id} />
            ))}
          </Fragment>
        ))}
      </ul>

      <div className={styles.loadExtra}>
        {isLoading ? (
          <>Loading reviews ...</>
        ) : (
          <>
            {hasNextPage && (
              <button onClick={fetchNextPage}>Show more reviews</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
